import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatDialogRef } from '@angular/material';
import { Router, RouterOutlet } from '@angular/router';
import { CanDeactivateGuard } from '@guards/can-deactivate-guard.service';
import { IMenuItem } from '@models/menu/menu-item';
import { MenuService } from '@services/menu.service';
import { isAlt, isAltLeftArrow, isCtrl, isEscape, isShift } from '@shared/key-codes';
import { fromEvent, Subject } from 'rxjs';
import { delay, distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { slideInAnimation } from './route-animation';

@Component({
  selector: 'app-dialog',
  exportAs: 'dialog',
  templateUrl: './dialog.component.html',
  styleUrls: ['./dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    slideInAnimation
    // animation triggers go here
  ]
})
export class DialogComponent implements OnInit, OnDestroy {
  public tabs: IMenuItem[] = [];
  public maximized = false;
  public halfSized = false;
  public isRightClickSwalOpen = false;
  private readonly destroyed$ = new Subject();

  get selectedTab() {
    return this.menu.selectedTab;
  }

  constructor(
    private readonly menu: MenuService,
    public dialogRef: MatDialogRef<DialogComponent>,
    public router: Router,
    private readonly canDeactivateGuardService: CanDeactivateGuard,
    private readonly location: Location
  ) {
    this.menu.setDialogRef(this.dialogRef);
    // Esc Key handling with Swal dialog open over the main dialog
    fromEvent(document, 'isRightClickSwalOpen')
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => (this.isRightClickSwalOpen = true)),
      fromEvent(document, 'isRightClickSwalClosed')
        .pipe(takeUntil(this.destroyed$), delay(10))
        .subscribe(() => (this.isRightClickSwalOpen = false)),
      // Handling click outside dialog
      dialogRef
        .backdropClick()
        .pipe(takeUntil(this.destroyed$))
        .subscribe(e => {
          e.preventDefault();
          this.close(e);
        }),
      // Handle maximization
      this.menu.maximized$.pipe(takeUntil(this.destroyed$)).subscribe(value => {
        if (this.maximized !== value) {
          this.maximized = value;
        }
      }),
      this.menu.halfSized$.pipe(takeUntil(this.destroyed$)).subscribe(value => {
        if (this.halfSized !== value) {
          this.halfSized = value;
        }
      });

    this.dialogRef.afterClosed().subscribe(result => {
      const selectedProduct = this.menu.selectedProduct;
      const selectedOption = this.menu.selectedOption;
      const selectedProductTab = this.menu.selectedProductTab;
      this.menu.selectedHeaderVal = true;

      if (selectedOption.group === 'all') {
        this.router.navigateByUrl(`${selectedOption.path}/${selectedProductTab ? selectedProductTab.Id : -1}`).catch(err => {
          console.error(err);
        });
      } else if (selectedOption.group === 'product') {
        this.router.navigateByUrl(`${selectedOption.path}/${selectedProduct.ProductId}`).catch(err => {
          console.error(err);
        });
      }
    });
  }

  ngOnInit() {
    this.tabs = this.menu.tabs;
    this.tabs[0].Selected = true;
    this.tabClicked(this.tabs[0]);
    this.initKeyboardEventsListener();
  }

  private initKeyboardEventsListener() {
    this.dialogRef
      .keydownEvents()
      .pipe(
        takeUntil(this.destroyed$),
        distinctUntilChanged(),
        // ignore alt, shiftKey, ctrl key presses as is the first key for a command combination
        filter(ke => !(isCtrl(ke) || isAlt(ke) || isShift(ke)))
      )
      .subscribe((ke: KeyboardEvent) => {
        if (isEscape(ke)) {
          // Prevent closing dialog if Escape key was pressed on any Spread dropdown
          // tslint:disable-next-line
          const path = event['path'].map(x => x.tagName);
          if (!(path.includes('GC-SPREAD-SHEETS') && path.includes('SELECT'))) {
            // tslint:disable-next-line
            this.close(event);
          }
          // Prevent closing dialog when pressing Alt+Left - Bug 3201
        } else if (isAltLeftArrow(ke)) {
          // tslint:disable-next-line
          event.preventDefault();
        }
      });
  }

  public ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  prepareRoute(outlet: RouterOutlet) {
    return outlet && outlet.activatedRouteData && outlet.activatedRouteData.animation;
  }

  public tabClicked(menuItem: IMenuItem): void {
    if (menuItem && menuItem.outlet && menuItem.routerLink) {
      const navigateParams = [{ outlets: { [menuItem.outlet]: [menuItem.routerLink] } }];
      this.router.navigate(navigateParams).then(nav => {
        /*
          If the result of our navigation was successful i.e. not blocked by
          a can deactivate, we will set this tab as selected.
        */
        if (nav) {
          this.tabs.forEach(tab => (tab.Selected = false));
          this.tabs.find(tab => tab.label === menuItem.label).Selected = true;

          this.location.replaceState(`/${menuItem.routerLink}`); // `/${menuItem.routerLink}`
        }
      });
    }
  }

  public toggleHalfSized() {
    this.menu.toggleHalfSized();
  }
  public toggleMaximized() {
    this.menu.toggleMaximized();
  }

  public onTitlebarDoubleClick() {
    this.menu.toggleMaximized();
  }

  public close(event) {
    // If 'Escape' key was pressed on alert dialog, stop processing it for the main dialog
    if (isEscape(event) && (event.target.className.match(/swal/) || this.isRightClickSwalOpen)) {
      // console.log('If is a escape over a swal dialog displayed, it avoids closing the dialog on the bubbling');
      return;
    }

    if (this.canDeactivateGuardService.canDeactivateTab) {
      if (this.menu.dialogSaveButton.enabled) {
        // Save & Discard buttons are enabled, so we need to discard changes before navigating ouf of tab
        this.menu.dialogSaveButton = {
          button: 'discard',
          clicked: true
        };
      }

      this.menu.closeDialog();
    } else {
      this.canDeactivateGuardService.canDeactivatePopupTab().then(res => {
        if (res) {
          if (this.menu.dialogSaveButton.enabled) {
            // Save & Discard buttons are enabled, so we need to discard changes before navigating ouf of tab
            this.menu.dialogSaveButton = {
              button: 'discard',
              clicked: true
            };
          }

          // On close of the dialog we have to fix the url
          const product = this.menu.selectedProduct;
          const selectedOption = this.menu.selectedOption;

          let url = '';
          if (selectedOption.group === 'all') {
            url = `${selectedOption.path}`;
          } else if (selectedOption.group === 'product') {
            url = `${selectedOption.path}/${product.ProductId}`;
          }
          this.location.replaceState(url);

          this.menu.closeDialog();
        }
      });
    }
  }

  public onDialogDiscardButtonClicked(msg: string) {
    this.canDeactivateGuardService.discardWarningMessage(msg);
  }

  public onDialogSaveButtonClicked() {
    this.menu.dialogSaveButton = {
      clicked: true,
      button: 'save',
      source: 'save button'
    };
  }

  public getDiscardMessage() {
    // You are about to discard the Forecast Market Rates changes you have made.
    // You are about to discard the Forecast Product changes you have made.
    // You are about to discard the Forecast Product and the Market Rates changes you have made.

    if (this.menu.productForecastStatus.unsavedChanges && this.menu.marketRateForecastStatus.unsavedChanges) {
      return 'Forecast Product and the Market Rates';
    } else if (this.menu.productForecastStatus.unsavedChanges) {
      return 'Forecast Product';
    } else if (this.menu.marketRateForecastStatus.unsavedChanges) {
      return 'Forecast Market Rates';
    }
  }
  public isDiscardEnabled() {
    return this.menu.dialogSaveButton.page !== null && this.menu.dialogSaveButton.unsavedChanges && !this.menu.dialogSaveButton.running;
  }

  public isSaveEnabled() {
    return this.menu.dialogSaveButton.enabled;
  }

  get isSaving() {
    return this.menu.dialogSaveButton.running;
  }

  public isTabDisabled(tab: IMenuItem): boolean {
    return this.selectedTab && this.selectedTab.routerLink === tab.routerLink;
  }
}
